import {
  AttachMoney,
  Business,
  Person,
  PersonOutline,
} from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { ScreenDetailsScheduleMobile } from "./ScreenDetailsScheduleMobile";
import { useFormatter } from "../../utils/useFormatter";
import dayjs from "dayjs";
import ScheduleCancelDialog from "./ScheduleCancelDialog";
import VideoConferencia from "../videoConferencia/VideoConferencia";
import { useSessionContext } from "../../contexts/SessionContext";
import { useFhirData } from "../../utils/useFhirData";
import { useLocationContext } from "../../contexts/LocationContext";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";

interface IProps {
  data: any;
  selected: any;
  setRefetch: any;
  onClick: () => void;
}

export function ScheduleCardsMobile({
  data,
  selected,
  setRefetch,
  onClick,
}: IProps) {
  const [openDetailsSchedule, setOpenDetailsSchedule] =
    useState<boolean>(false);
  const [scheduleCancelDialog, setScheduleCancelDialog] =
    useState<boolean>(false);
  const [hasPaymentLink, setHasPaymentLink] = useState<boolean>(false);
  const [payment, setPayment] = useState<any>([]);
  const [isPaid, setIsPaid] = useState<boolean>(false);

  const { userPatient } = useSessionContext();
  const { location } = useLocationContext();

  useEffect(() => {
    const checkPaymentLink = async () => {
      try {
        if (data?.slot?.split("/")?.[1]) {
          await marketplaceService.getPaymentLinkBySlotId(
            data?.slot?.split("/")?.[1],
          );
          setHasPaymentLink(true);
        }
      } catch (error) {
        console.error("Erro ao verificar link de pagamento:", error);
        setHasPaymentLink(false);
      }
    };

    if (data?.status === "pending" && data?.slot) {
      checkPaymentLink();
    }
  }, [data?.slot, data?.status]);

  useEffect(() => {
    const fetchPayment = async () => {
      if (data?.id && userPatient?.id) {
        try {
          const slotReference = data.slot;
          const patientReference = `Patient/${userPatient.id}`;

          if (slotReference && patientReference) {
            const filter = `status=active&_sort=-_lastUpdated&request=${slotReference}&response=${patientReference}&_count=1`;
            const response = await GetPaymentNotice({ filter });

            if (response?.data?.entry) {
              setPayment(response.data.entry);
              const hasPaymentStatus =
                response.data.entry[0]?.resource?.paymentStatus;
              setIsPaid(!!hasPaymentStatus);
            } else {
              setPayment([]);
              setIsPaid(false);
            }
          }
        } catch (error) {
          console.error("Erro ao buscar pagamento:", error);
          setPayment([]);
          setIsPaid(false);
        }
      }
    };

    fetchPayment();
  }, [data?.id, data?.slot, userPatient?.id]);

  // Localização completa com extensions para este agendamento
  const mobileLocationResource = location?.find((loc: any) =>
    loc?.resource?.id.includes(data?.locationId?.id),
  )?.resource;

  const locationTeleconsulting = useFhirData.virtualService(mobileLocationResource);

  /**
   * Canal de atendimento desta agenda (ex: "chat", "teleatendimento").
   * Lido da extension attendance-channel da Location do card.
   */
  const locationAttendanceChannel = mobileLocationResource?.extension?.find(
    (ext: any) => ext.url?.includes("attendance-channel"),
  )?.valueCode as string | undefined;

  const handleOpenCancelDialog = () => {
    setScheduleCancelDialog(true);
  };

  return (
    <>
      <Box
        minWidth="280px"
        width="100%"
        sx={{
          border: "1px solid",
          borderColor:
            data?.status === "proposed"
              ? "warning500.main"
              : data?.status === "pending"
                ? "neutral400.main"
                : "primary300.main",
          borderRadius: "5px",
          boxShadow: 3,
          position: "relative",
        }}
      >
        {data?.status === "pending" && hasPaymentLink && (
          <Button
            sx={{
              position: "absolute",
              top: "30px",
              right: "10px",
              zIndex: 1,
              minWidth: "auto",
              padding: "4px 8px",
              backgroundColor: "neutral600.main",
              borderRadius: "8px",
              fontSize: "0.75rem",
            }}
            size="small"
            variant="contained"
            onClick={async () => {
              const linkRedirect =
                await marketplaceService.getPaymentLinkBySlotId(
                  data?.slot.split("/")?.[1],
                );
              window.open(linkRedirect, "_blank");
            }}
          >
            <AttachMoney fontSize="small" /> Pagar
          </Button>
        )}

        <Stack
          sx={{
            backgroundColor:
              data?.status === "pending"
                ? "neutral400.main" // mantém o cinza atual
                : data?.status === "proposed"
                  ? "warning100.main"
                  : "primary300.main",
            padding: "12px",
          }}
        >
          <Typography variant="body1">
            Consulta de {data?.healthcare}
          </Typography>
          <Typography variant="body1">
            <li>{dayjs(data?.startTime).format("DD MMM YYYY - HH:mm")}</li>
          </Typography>
        </Stack>

        <Stack padding="12px">
          <Stack direction="row" gap={1}>
            <Business />
            <Stack
              width="100%"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                noWrap
                sx={{ width: "70%", textOverflow: "ellipsis" }}
                variant="body2"
                color="neutral700.main"
              >
                {useFormatter.formatNameDisplay(data.location)}
              </Typography>
              <Typography
                variant="body2"
                color="primary300.main"
                onClick={() => {
                  setOpenDetailsSchedule(true);
                  onClick();
                }}
              >
                Mais...
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" mb={1} gap={1} alignItems="center">
            <Person />
            <Typography variant="body2" color="neutral700.main">
              {data?.practitioner}
            </Typography>
          </Stack>

          <Stack direction="row" mb={1} gap={1} alignItems="center">
            <PersonOutline />
            <Typography variant="body2" color="neutral700.main">
              {data?.patientName}
            </Typography>
          </Stack>
          {/* Com "Pagar" visível (pendente + link), não mostrar chat/tele — prioriza o pagamento */}
          {locationTeleconsulting &&
            !(data?.status === "pending" && hasPaymentLink) && (
            <VideoConferencia
              roomUrl={data?.virtualService}
              cardSchedule
              isPending={data?.status === "pending"}
              attendanceType={locationAttendanceChannel}
              appointmentData={{
                id: data.id,
                patientName: data.patientName,
                practitionerName: data.practitioner,
                startTime: data.startTime,
              }}
            />
          )}
        </Stack>
      </Box>
      <ScreenDetailsScheduleMobile
        open={openDetailsSchedule}
        isVirtual={locationTeleconsulting}
        onClose={() => setOpenDetailsSchedule(false)}
        setScheduleCancelDialog={handleOpenCancelDialog}
        data={data}
        payment={payment}
        isPaid={isPaid}
      />
      <ScheduleCancelDialog
        appointment={data}
        isOpen={scheduleCancelDialog}
        setIsOpen={setScheduleCancelDialog}
        setRefetch={setRefetch}
        payment={payment}
        isPaid={isPaid}
      />
    </>
  );
}
